import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import MonthlyBillingRun from "@/components/MonthlyBillingRun";
import AdminBilling from "@/components/AdminBilling";
import AdminCreditInsights from "@/components/AdminCreditInsights";
import ProfitReport from "@/components/ProfitReport";
import { CalendarClock, SlidersHorizontal, TrendingUp, Wallet, Coins, Save, Loader2, Plus, Trash2 } from "lucide-react";

const TABS = [
  { v: "run", l: "Run the month", d: "Review, credit, lock, invoice", icon: CalendarClock },
  { v: "rates", l: "Rate cards", d: "Set-once pricing per client", icon: SlidersHorizontal },
  { v: "costs", l: "Our costs", d: "What fulfillment costs us", icon: Coins, superOnly: true },
  { v: "profit", l: "Profit", d: "Markup & margin by client", icon: TrendingUp, superOnly: true },
  { v: "credits", l: "Credits & claims", d: "What we gave back", icon: Wallet },
];

/** One home for everything billing: the monthly run, pricing, our costs and profit. */
export default function BillingPortal({ accounts = [], initialTab = "run", isSuperAdmin = false, showRail = true }) {
  const [tab, setTab] = useState(initialTab);
  useEffect(() => { setTab(initialTab); }, [initialTab]);
  const tabs = TABS.filter((t) => !t.superOnly || isSuperAdmin);

  return (
    <div className="space-y-4" data-testid="billing-portal">
      {showRail && (
      <div className="bg-white border border-slate-200 rounded-xl p-1.5 flex flex-wrap gap-1" data-testid="billing-portal-rail">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.v;
          return (
            <button
              key={t.v}
              data-testid={`portal-tab-${t.v}`}
              onClick={() => setTab(t.v)}
              className={`flex-1 min-w-[150px] text-left px-3.5 py-2.5 rounded-lg transition-colors ${active ? "bg-bh-blue text-white" : "hover:bg-slate-50 text-slate-700"}`}
            >
              <span className="flex items-center gap-2 font-semibold text-sm">
                <Icon className="w-4 h-4" /> {t.l}
              </span>
              <span className={`block text-xs mt-0.5 ${active ? "text-white/75" : "text-slate-400"}`}>{t.d}</span>
            </button>
          );
        })}
      </div>
      )}

      {tab === "run" && <MonthlyBillingRun accounts={accounts} />}
      {tab === "rates" && <AdminBilling accounts={accounts} view="rate_card" />}
      {tab === "costs" && isSuperAdmin && <InternalCosts accounts={accounts} />}
      {tab === "profit" && isSuperAdmin && <ProfitReport />}
      {tab === "credits" && <AdminCreditInsights />}
    </div>
  );
}

const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500";

/** Our internal cost of doing the work — global defaults with optional per-client overrides.
 *  Admin-only: it never appears on a customer's bill. */
function InternalCosts({ accounts = [] }) {
  const [accountId, setAccountId] = useState("");
  const [costs, setCosts] = useState(null);
  const [meta, setMeta] = useState({ scope: "global", has_override: false });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/internal-costs${accountId ? `?account_id=${accountId}` : ""}`);
    setCosts({
      cost_per_order: data.costs.cost_per_order ?? 0,
      cost_per_unit: data.costs.cost_per_unit ?? 0,
      packaging_costs: (data.costs.packaging_costs || []).map((p) => ({ ...p, key: Math.random().toString(36).slice(2) })),
    });
    setMeta({ scope: data.scope, has_override: !!data.has_override });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/internal-costs${accountId ? `?account_id=${accountId}` : ""}`, {
        cost_per_order: Number(costs.cost_per_order) || 0,
        cost_per_unit: Number(costs.cost_per_unit) || 0,
        packaging_costs: costs.packaging_costs.filter((p) => (p.package_type || "").trim()).map((p) => ({ package_type: p.package_type.trim(), amount: Number(p.amount) || 0 })),
      });
      toast.success(accountId ? "Client cost override saved" : "Global costs saved");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const clearOverride = async () => {
    try {
      await api.delete(`/admin/internal-costs?account_id=${accountId}`);
      toast.success("Back to the global costs");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!costs) return <div className="text-slate-400 text-sm py-8">Loading…</div>;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5" data-testid="internal-costs">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900">What the work costs us</h3>
          <p className="text-xs text-slate-500 mt-0.5">Used only for the profit report — never billed and never shown to a customer. Carrier cost comes straight from ShipStation.</p>
        </div>
        <select data-testid="costs-account-select" value={accountId} onChange={(e) => setAccountId(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
          <option value="">Global default (all clients)</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.company_name}</option>)}
        </select>
      </div>

      {accountId && (
        <p className={`text-xs rounded-md px-3 py-2 ${meta.has_override ? "bg-blue-50 border border-blue-200 text-blue-900" : "bg-slate-50 border border-slate-200 text-slate-600"}`} data-testid="costs-scope-note">
          {meta.has_override
            ? "This client has its own costs — the global default is ignored for them."
            : "Showing the global default. Save to give this client their own costs."}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Our cost per order</label>
          <input data-testid="cost-per-order" type="number" step="0.01" value={costs.cost_per_order} onChange={(e) => setCosts((c) => ({ ...c, cost_per_order: e.target.value }))} className={inputCls} />
          <p className="text-xs text-slate-400 mt-1">Labour to pick, pack and hand off one order.</p>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Our cost per unit</label>
          <input data-testid="cost-per-unit" type="number" step="0.01" value={costs.cost_per_unit} onChange={(e) => setCosts((c) => ({ ...c, cost_per_unit: e.target.value }))} className={inputCls} />
          <p className="text-xs text-slate-400 mt-1">Extra handling for each additional unit in an order.</p>
        </div>
      </div>

      <div>
        <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-2">What each package type costs us</label>
        <div className="space-y-2">
          {costs.packaging_costs.map((p, i) => (
            <div key={p.key} className="flex items-center gap-2" data-testid={`cost-pkg-row-${i}`}>
              <input data-testid={`cost-pkg-type-${i}`} value={p.package_type || ""} onChange={(e) => setCosts((c) => ({ ...c, packaging_costs: c.packaging_costs.map((x, idx) => (idx === i ? { ...x, package_type: e.target.value } : x)) }))} placeholder="package / small_poly" className="flex-1 px-3 py-1.5 border border-slate-300 rounded text-sm" />
              <input data-testid={`cost-pkg-amount-${i}`} type="number" step="0.01" value={p.amount ?? 0} onChange={(e) => setCosts((c) => ({ ...c, packaging_costs: c.packaging_costs.map((x, idx) => (idx === i ? { ...x, amount: e.target.value } : x)) }))} className="w-28 px-3 py-1.5 border border-slate-300 rounded text-sm" />
              <button onClick={() => setCosts((c) => ({ ...c, packaging_costs: c.packaging_costs.filter((_, idx) => idx !== i) }))} className="text-red-500 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button data-testid="add-cost-pkg" onClick={() => setCosts((c) => ({ ...c, packaging_costs: [...c.packaging_costs, { package_type: "", amount: 0, key: Math.random().toString(36).slice(2) }] }))} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-semibold">
            <Plus className="w-4 h-4" /> Add package cost
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
        <button data-testid="save-costs-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-bh-blue hover:opacity-90 disabled:opacity-60 text-white font-semibold px-5 py-2 rounded-md transition-opacity">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save {accountId ? "for this client" : "global costs"}
        </button>
        {accountId && meta.has_override && (
          <button data-testid="clear-costs-override" onClick={clearOverride} className="text-sm font-semibold text-slate-500 hover:text-red-600">Use the global costs instead</button>
        )}
        <span className="text-xs text-slate-400 ml-auto">
          Example: {formatCurrency(Number(costs.cost_per_order) || 0)} + {formatCurrency(Number(costs.cost_per_unit) || 0)} per unit
        </span>
      </div>
    </div>
  );
}
